import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import HeaderBar from '../CrystalContainer/HeaderBar';
import RMTable, { RowExtraInfo } from 'components/customComponents/Controls/RMTable';
import { useCaptureProfiles, useSelectedImagingSchedule } from './hooks';
import './Imaging.scss';
import { TableColumns } from './constants';
import {
  ImagingSchedulesDTO,
  ImagingSchedulesDeletedDataDTO,
  ImagingSchedulesEntryDTO,
} from 'business/models/Models';
import {
  addEntryCaptureProfile,
  getRandomNegativeNumber,
  removeEntryCaptureProfile,
} from './utils';
import RMCheckBox from 'components/customComponents/Controls/tableSelectionCheckBox';
import { ScheduleRowInfo } from './types';
import { tableFilter } from './tableFilter';
import { setImagingSchedulesIsDirty } from 'redux/reducers/imagingSchedulesReducer';
import { useDispatch } from 'react-redux';
import { useImagingSchedulesIsDirty } from 'redux/hooks/imagingSetup';
import { RMModalProps } from 'components/modal';

interface ScheduleDetailsProps {
  imagingSchedules: ImagingSchedulesDTO[];
  setImagingScheduleDto: React.Dispatch<React.SetStateAction<ImagingSchedulesDTO[]>>;
  setDeletedData: React.Dispatch<React.SetStateAction<ImagingSchedulesDeletedDataDTO>>;
  scheduleErrors?: ScheduleRowInfo;
  setModal?: React.Dispatch<React.SetStateAction<RMModalProps>>;
}

function ScheduleDetails(props: ScheduleDetailsProps) {
  const { imagingSchedules, setImagingScheduleDto, scheduleErrors, setDeletedData } =
    props;
  const selectedImgSchId = useSelectedImagingSchedule();
  const { captureProfiles } = useCaptureProfiles();
  const [selectedIds, setSelectedIds] = useState<number[]>();
  const [entryErrors, setEntryErrors] = useState<RowExtraInfo>({});
  const [editId, setEditId] = useState<number>();
  const isDirty = useImagingSchedulesIsDirty();
  const dispatch = useDispatch();
  const [selectAll, setSelectAll] = useState<{
    [key: string]: boolean;
  }>({});
  const inputRef = useRef<HTMLInputElement | null>(null);

  const onCellChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, id: number, cpId?: number) => {
      const { name, value, type } = e.target;
      setImagingScheduleDto((prev) => {
        return prev.map((schedule) => {
          if (schedule.id === selectedImgSchId) {
            const updatedEntries = schedule.imagingScheduleEntry.map((entry) => {
              if (entry.id === id) {
                if (type === 'checkbox' && name === 'notify') {
                  return new ImagingSchedulesEntryDTO({
                    ...entry,
                    [name]: !entry[name],
                  });
                } else if (type === 'checkbox' && name === 'captureProfile') {
                  const updatedEntry = new ImagingSchedulesEntryDTO(entry);
                  entry.imagingScheduleEntryCaptureProfile.some(
                    (profile) => profile.captureProfileId === cpId,
                  )
                    ? removeEntryCaptureProfile(updatedEntry, cpId, setDeletedData)
                    : addEntryCaptureProfile(updatedEntry, cpId);
                  return updatedEntry;
                } else if (type === 'number') {
                  return new ImagingSchedulesEntryDTO({
                    ...entry,
                    [name]: parseInt(value, 10),
                  });
                } else {
                  return new ImagingSchedulesEntryDTO({
                    ...entry,
                    [name]: value,
                  });
                }
              }
              return entry;
            });

            return new ImagingSchedulesDTO({
              ...schedule,
              imagingScheduleEntry: updatedEntries,
            });
          }
          return schedule;
        });
      });

      dispatch(setImagingSchedulesIsDirty(true));
    },
    [[imagingSchedules, selectedImgSchId]],
  );

  const handleHeaderCheckboxToggle = (
    e: React.ChangeEvent<HTMLInputElement>,
    cpId: number,
  ) => {
    setSelectAll((prevState) => ({
      ...prevState,
      [cpId]: !prevState[cpId],
    }));
    setImagingScheduleDto((prev) => {
      return prev.map((schedule) => {
        if (schedule.id === selectedImgSchId) {
          const updatedEntries = schedule.imagingScheduleEntry.map((entry) => {
            const updatedEntry = new ImagingSchedulesEntryDTO(entry);

            if (selectAll[cpId]) {
              removeEntryCaptureProfile(updatedEntry, cpId, setDeletedData);
            } else {
              addEntryCaptureProfile(updatedEntry, cpId);
            }

            return updatedEntry;
          });

          return new ImagingSchedulesDTO({
            ...schedule,
            imagingScheduleEntry: updatedEntries,
          });
        }
        return schedule;
      });
    });

    dispatch(setImagingSchedulesIsDirty(true));
  };

  const getIndeterminate = useCallback(
    (cpId: number) => {
      const imagingScheduleEntries = imagingSchedules?.find(
        (x) => x.id === selectedImgSchId,
      )?.imagingScheduleEntry;
      const numChecked = imagingScheduleEntries?.filter((entry) =>
        entry.imagingScheduleEntryCaptureProfile.find(
          (profile) => profile.captureProfileId === cpId,
        ),
      ).length;
      return numChecked > 0 && numChecked < imagingScheduleEntries?.length;
    },
    [imagingSchedules, selectedImgSchId],
  );

  const ScheduleDetailsCols: TableColumns[] = [
    {
      Header: 'Days',
      accessor: 'days',
      className: 'scheduleData',
      Filter: tableFilter(false, 'Days'),
    },
    {
      Header: 'Hours',
      accessor: 'hours',
      className: 'scheduleData',
      Filter: tableFilter(false, 'Hours'),
    },
    {
      Header: 'Notify',
      accessor: 'notify',
      className: 'scheduleData alignLeft',
      Cell: ({ cell }) => (
        <input
          id="scheduledetails-checkbox-notify"
          type="checkbox"
          name="notify"
          checked={cell.value === true}
          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
          onChange={(e) => onCellChange(e, cell.row.original.id)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      Header: 'Priority',
      accessor: 'priority',
      className: 'priorityData alignLeft',
      Filter: tableFilter(false, 'Priority'),
    },
    ...(captureProfiles || []).map((cp) => ({
      accessor: cp.id.toString(),
      Header: () => (
        <>
          <RMCheckBox
            checked={selectAll[cp.id] || false}
            onChange={(e) => handleHeaderCheckboxToggle(e, cp.id)}
            indeterminate={getIndeterminate(cp.id)}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
          />
          <span id="scheduledetails-captureprofiles-name">{cp.name}</span>
        </>
      ),
      className: 'alignLeft gap-1',
      Cell: ({ cell }) => {
        return (
          <input
            id="scheduledetails-checkbox-captureProfile"
            type="checkbox"
            name="captureProfile"
            checked={cell.row.original.imagingScheduleEntryCaptureProfile.find(
              (d) => d.captureProfileId === parseInt(cell.column.id, 10),
            )}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) =>
              onCellChange(e, cell.row.original.id, parseInt(cell.column.id, 10))
            }
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
          />
        );
      },
    })),
  ];

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setEditId(undefined);
    }
  };

  const renderDaysHoursPriority = (id: number, name: string, value: any) => {
    return id !== editId ? (
      <div
        id="scheduledetails-renderdayshourspriority-value"
        className="!border-0 bg-transparent p-2 w-full min-h-[2rem]"
      >
        {value}
      </div>
    ) : (
      <input
        key={id}
        id="scheduledetails-renderdayshourspriority-input"
        type="number"
        name={name}
        value={value}
        min={0}
        className="bg-white p-2 w-full"
        style={{ border: '1px solid black', borderRadius: '0.25rem' }}
        onChange={(e) => onCellChange(e, id)}
        onClick={(e) => e.stopPropagation()}
        ref={name === 'days' ? inputRef : null}
        onKeyDown={handleKeyPress}
      />
    );
  };

  const tableData = useMemo(() => {
    const imagingScheduleEntries = imagingSchedules?.find(
      (x) => x.id === selectedImgSchId,
    )?.imagingScheduleEntry;
    if (imagingScheduleEntries) {
      return imagingScheduleEntries?.map((entry: ImagingSchedulesEntryDTO, _idx) => ({
        id: entry.id,
        days: renderDaysHoursPriority(entry.id, 'days', entry.days),
        hours: renderDaysHoursPriority(entry.id, 'hours', entry.hours),
        notify: entry.notify,
        priority: renderDaysHoursPriority(entry.id, 'priority', entry.priority),
        imagingScheduleEntryCaptureProfile: entry.imagingScheduleEntryCaptureProfile,
      }));
    }
    return [];
  }, [imagingSchedules, selectedImgSchId, editId]);

  const handleOnRowClick = (row) => {
    setSelectedIds([row.index]);
    setEditId(undefined);
  };

  const onAdd = () => {
    const rndNum = getRandomNegativeNumber();
    const imagingScheduleEntries = imagingSchedules?.find(
      (x) => x.id === selectedImgSchId,
    )?.imagingScheduleEntry;
    const newData = [...(imagingScheduleEntries ?? [])];
    const newDataItem = new ImagingSchedulesEntryDTO({
      id: rndNum,
      days: 0,
      hours: 0,
      notify: true,
      priority: newData?.length + 1,
      imagingScheduleEntryCaptureProfile: [],
    });
    newData.push(newDataItem);
    setImagingScheduleDto((prev) => {
      return prev.map((schedule) => {
        if (schedule.id === selectedImgSchId) {
          const updatedSchedule = {
            ...schedule,
            imagingScheduleEntry: [...newData],
          };
          return new ImagingSchedulesDTO(updatedSchedule);
        }
        return schedule;
      });
    });
    setEditId(rndNum);
    setTimeout(() => {
      if (inputRef?.current) {
        inputRef.current.focus();
      }
    }, 200);
    dispatch(setImagingSchedulesIsDirty(true));
  };

  const onEdit = () => {
    const imagingScheduleEntries = imagingSchedules?.find(
      (x) => x.id === selectedImgSchId,
    )?.imagingScheduleEntry;
    setEditId(imagingScheduleEntries[selectedIds[0]]?.id);
    setTimeout(() => {
      if (inputRef?.current) {
        inputRef.current.focus();
      }
    }, 200);
  };

  const onDelete = async () => {
    const imagingScheduleEntries = imagingSchedules?.find(
      (x) => x.id === selectedImgSchId,
    )?.imagingScheduleEntry;
    const ids = selectedIds.map((i) => imagingScheduleEntries[i]?.id);
    const existingIds = ids.filter((id) => id > 0);
    setImagingScheduleDto((prev) => {
      return prev.map((schedule) => {
        if (schedule.id === selectedImgSchId) {
          const updatedEntry = schedule.imagingScheduleEntry.filter(
            (x) => !ids.includes(x.id),
          );
          return new ImagingSchedulesDTO({
            ...schedule,
            imagingScheduleEntry: updatedEntry,
          });
        }
        return schedule;
      });
    });

    setDeletedData(
      (prevData) =>
        new ImagingSchedulesDeletedDataDTO({
          ...prevData,
          imagingScheduleEntryIds: Array.from(
            new Set([...(prevData?.imagingScheduleEntryIds || []), ...existingIds]),
          ),
        }),
    );
    dispatch(setImagingSchedulesIsDirty(true));
    setSelectedIds([0]);
  };

  useEffect(() => {
    const imagingScheduleEntries = imagingSchedules?.find(
      (x) => x.id === selectedImgSchId,
    )?.imagingScheduleEntry;
    if (!imagingScheduleEntries || imagingScheduleEntries.length === 0) {
      setSelectAll({});
      return;
    }
    const profileCount = {};
    imagingScheduleEntries.forEach((entry) => {
      entry.imagingScheduleEntryCaptureProfile.forEach((cp) => {
        profileCount[cp.captureProfileId] = (profileCount[cp.captureProfileId] || 0) + 1;
      });
    });
    const newSelectAll = {};
    imagingScheduleEntries.forEach((entry) => {
      entry.imagingScheduleEntryCaptureProfile.forEach((cp) => {
        newSelectAll[cp.captureProfileId] =
          profileCount[cp.captureProfileId] === imagingScheduleEntries.length;
      });
    });
    setSelectAll(newSelectAll);
  }, [imagingSchedules, selectedImgSchId]);

  useEffect(() => {
    if (scheduleErrors && imagingSchedules && selectedImgSchId) {
      const index = imagingSchedules.findIndex((x) => x.id === selectedImgSchId);
      setEntryErrors(scheduleErrors[index]?.entryErrors);
    }
  }, [scheduleErrors, selectedImgSchId, imagingSchedules]);

  useEffect(() => {
    if (!isDirty) {
      setEditId(undefined);
    }
  }, [isDirty]);

  return (
    <div id="scheduledetails-wrapper" className="flex flex-col h-full">
      <HeaderBar
        title="Schedule Details"
        onAdd={onAdd}
        onEdit={onEdit}
        onDelete={() => onDelete()}
        enableEditDelete={selectedIds?.length > 0 && !!selectedImgSchId}
        disableAdd={!selectedImgSchId}
      />
      <RMTable
        columns={ScheduleDetailsCols}
        data={tableData}
        tableClass="w-full"
        rowExtraInfo={entryErrors}
        selectedIds={selectedIds}
        onRowClicked={handleOnRowClick}
      />
    </div>
  );
}

export default ScheduleDetails;
